import { generateClient } from 'aws-amplify/api'
import { env } from '../config/env'
import { getAccessToken } from './auth-service'
import { serializeAwsJson } from '../utils/aws-json'
import { getDeviceId } from '../utils/device-id'
import { formatUnknownError } from '../utils/format-error'
import type { PublishVoiceCallSignalInput, VoiceCallSignal } from '../types/voice-call'
import type { DeviceClaimCap } from '@shared/calls/device-claim-liveness'

const client = generateClient()

function assertAppSyncConfigured() {
  if (!env.appsyncGraphqlUrl) {
    throw new Error(
      'VITE_APPSYNC_GRAPHQL_URL is not set. Run infrastructure/scripts/deploy-chat.sh first.',
    )
  }
}

const SIGNAL_FIELDS = /* GraphQL */ `
  callId
  type
  callerId
  calleeId
  senderId
  recipientUserId
  senderDeviceId
  payload
  conversationId
  createdAt
  roomCallId
  roomInvite
  joinCallId
`

const publishVoiceCallSignalMutation = /* GraphQL */ `
  mutation PublishVoiceCallSignal($input: PublishVoiceCallSignalInput!) {
    publishVoiceCallSignal(input: $input) {
      ${SIGNAL_FIELDS}
    }
  }
`

export const onVoiceCallSignalSubscription = /* GraphQL */ `
  subscription OnVoiceCallSignal($recipientUserId: ID!) {
    onVoiceCallSignal(recipientUserId: $recipientUserId) {
      ${SIGNAL_FIELDS}
    }
  }
`

/** Per-call room channel — every subscriber with this callId gets in-room signals. */
export const onCallRoomSignalSubscription = /* GraphQL */ `
  subscription OnCallRoomSignal($callId: ID!) {
    onCallRoomSignal(callId: $callId) {
      ${SIGNAL_FIELDS}
    }
  }
`

const myActiveCallQuery = /* GraphQL */ `
  query MyActiveCall {
    myActiveCall {
      callId
      callerId
      calleeId
      conversationId
      mediaType
      callPurpose
      startedAt
      updatedAt
      accepted
      activeDeviceId
      activeDevicePlatform
    }
  }
`

/** Backend view of the user's live 1:1 call (SESSION row) — see myActiveCall. */
export type ActiveCallSession = {
  callId: string
  callerId: string
  calleeId: string
  conversationId: string | null
  mediaType: 'voice' | 'video' | null
  callPurpose: 'normal' | 'on-call' | 'urgent' | null
  startedAt: string
  updatedAt: string | null
  /** True when an ACCEPT was recorded — the call is a live conversation. */
  accepted: boolean
  /**
   * THIS user's heartbeat-fresh device claim, or null. Never another user's
   * device, and never a stale/closed-tab claim — missing/stale means "no
   * switch prompt".
   */
  activeDeviceId: string | null
  /** 'ios' | 'android' | 'web' of this user's claimed device (when fresh). */
  activeDevicePlatform: 'ios' | 'android' | 'web' | null
}

/**
 * Ask the backend (the durable source of truth) whether this user is in a live
 * call right now. Used on app foreground/cold-open to re-hydrate the voice-call
 * session after the in-memory store died with the WebView — without this, a
 * user could be IN a call with no way back to it (Sprint 8 root-cause report).
 * Returns null when there is no live call or the query fails (best-effort:
 * rehydration is a recovery path and must never break normal startup).
 */
export async function fetchMyActiveCall(): Promise<ActiveCallSession | null> {
  assertAppSyncConfigured()
  try {
    const result = await client.graphql({
      query: myActiveCallQuery,
      authMode: 'userPool',
    })
    if ('errors' in result && result.errors?.length) return null
    const session = (result as { data?: { myActiveCall?: ActiveCallSession | null } }).data
      ?.myActiveCall
    if (!session?.callId) return null
    return {
      ...session,
      mediaType: session.mediaType === 'video' ? 'video' : session.mediaType ? 'voice' : null,
      accepted: Boolean(session.accepted),
      activeDeviceId: session.activeDeviceId ?? null,
      activeDevicePlatform:
        session.activeDevicePlatform === 'ios' ||
        session.activeDevicePlatform === 'android' ||
        session.activeDevicePlatform === 'web'
          ? session.activeDevicePlatform
          : null,
    }
  } catch {
    return null
  }
}

// Separate from myActiveCallQuery on purpose: a client that ships before the
// schema field is live must never break the rehydrate query. `callId` makes
// the resolver read that call's row directly (no table scan).
const myActiveCallByIdQuery = /* GraphQL */ `
  query MyActiveCallById($callId: ID) {
    myActiveCall(callId: $callId) {
      callId
      sessionUuid
    }
  }
`

/**
 * The user's live call `callId` as the server holds it (heartbeat-fresh), or
 * null when it is gone / not theirs / the query fails.
 */
export async function fetchActiveCallById(
  callId: string,
): Promise<{ callId: string; sessionUuid: string | null } | null> {
  assertAppSyncConfigured()
  try {
    const result = await client.graphql({
      query: myActiveCallByIdQuery,
      variables: { callId },
      authMode: 'userPool',
    })
    if ('errors' in result && result.errors?.length) return null
    const live = (result as { data?: { myActiveCall?: { callId?: string; sessionUuid?: string | null } | null } })
      .data?.myActiveCall
    return live?.callId === callId ? { callId, sessionUuid: live.sessionUuid ?? null } : null
  } catch {
    return null
  }
}

/**
 * The telehealth session of the user's live call `callId` — for rings rebuilt
 * from a native push (no INVITE payload in JS). Null when it is not a
 * session call, not live, or the query fails.
 */
export async function fetchActiveCallSessionUuid(callId: string): Promise<string | null> {
  return (await fetchActiveCallById(callId))?.sessionUuid ?? null
}

function toGraphqlInput(input: PublishVoiceCallSignalInput): PublishVoiceCallSignalInput {
  // Always tag the originating device so other devices can ignore our echoes
  // and detect "answered/declined on another device".
  const withDevice = { ...input, senderDeviceId: input.senderDeviceId ?? getDeviceId() }
  const { payload, ...rest } = withDevice
  if (payload == null) return rest
  if (typeof payload === 'string') return { ...rest, payload }
  return { ...rest, payload: serializeAwsJson(payload) }
}

export async function publishVoiceCallSignal(
  input: PublishVoiceCallSignalInput,
): Promise<VoiceCallSignal> {
  assertAppSyncConfigured()
  try {
    const result = await client.graphql({
      query: publishVoiceCallSignalMutation,
      variables: { input: toGraphqlInput(input) },
      authMode: 'userPool',
    })
    if ('errors' in result && result.errors?.length) {
      throw new Error(result.errors.map((e) => formatUnknownError(e)).join('; '))
    }
    const signal = (result as { data?: { publishVoiceCallSignal?: VoiceCallSignal } }).data
      ?.publishVoiceCallSignal
    if (!signal) throw new Error('Empty voice call signal response')
    return signal
  } catch (err) {
    throw new Error(formatUnknownError(err))
  }
}

export type DeviceClaimAction = 'claim' | 'takeover' | 'media_started' | 'release'

/** Persist / fan-out media ownership for this user's devices (SESSION# only). */
export async function publishDeviceClaim(input: {
  callId: string
  callerId: string
  calleeId: string
  conversationId?: string | null
  action: DeviceClaimAction
  platform: 'ios' | 'android' | 'web'
  /** Device that should own media after this claim (written to SESSION). */
  deviceId: string
  /**
   * Device that is publishing the signal. Defaults to `deviceId`. Pass the
   * local device id when restoring a previous owner's claim on rollback so
   * siblings still receive the event (self-signals are filtered by senderDeviceId).
   */
  senderDeviceId?: string
  /** Capabilities stored on the claim (`advertisedDeviceClaimCaps`). */
  caps?: readonly DeviceClaimCap[]
}): Promise<VoiceCallSignal> {
  return publishVoiceCallSignal({
    callId: input.callId,
    type: 'DEVICE_CLAIM',
    callerId: input.callerId,
    calleeId: input.calleeId,
    conversationId: input.conversationId,
    senderDeviceId: input.senderDeviceId ?? input.deviceId,
    payload: {
      action: input.action,
      platform: input.platform,
      deviceId: input.deviceId,
      ...(input.caps?.length ? { caps: [...input.caps] } : {}),
    },
  })
}

function parseJsonValue(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

export function parseSignalPayload<T>(
  payload: string | Record<string, unknown> | null | undefined,
): T | null {
  if (payload == null) return null

  let parsed: unknown = payload
  for (let depth = 0; depth < 4 && typeof parsed === 'string'; depth += 1) {
    parsed = parseJsonValue(parsed)
    if (parsed == null) return null
  }

  if (typeof parsed !== 'object' || parsed == null) return null

  const record = parsed as Record<string, unknown>
  const nested = record.payload
  if (nested != null && (typeof nested === 'string' || typeof nested === 'object')) {
    const inner = parseSignalPayload<T>(nested as string | Record<string, unknown>)
    if (inner) return inner
  }

  return record as T
}

export type CallerInvitePayload = {
  callerDisplayName?: string
  callerAvatarUrl?: string | null
  mediaType?: 'voice' | 'video'
  callPurpose?: 'normal' | 'on-call' | 'urgent'
  urgentLocation?: { lat: number; lng: number; label?: string } | null
  /** Telehealth session this call is (validated server-side on INVITE). */
  sessionUuid?: string
}

export function extractCallerInvitePayload(
  payload: string | Record<string, unknown> | null | undefined,
): CallerInvitePayload | null {
  const parsed = parseSignalPayload<Record<string, unknown>>(payload)
  if (!parsed) return null

  const displayName =
    (typeof parsed.callerDisplayName === 'string' && parsed.callerDisplayName) ||
    (typeof parsed.caller_display_name === 'string' && parsed.caller_display_name) ||
    undefined

  const avatarUrl =
    (typeof parsed.callerAvatarUrl === 'string' && parsed.callerAvatarUrl) ||
    (typeof parsed.caller_avatar_url === 'string' && parsed.caller_avatar_url) ||
    (parsed.callerAvatarUrl === null ? null : undefined)

  const mediaType =
    parsed.mediaType === 'video' || parsed.mediaType === 'voice'
      ? parsed.mediaType
      : undefined

  const callPurpose =
    parsed.callPurpose === 'on-call' || parsed.callPurpose === 'urgent' || parsed.callPurpose === 'normal'
      ? parsed.callPurpose
      : undefined

  const urgentLocation =
    parsed.urgentLocation &&
    typeof parsed.urgentLocation === 'object' &&
    typeof (parsed.urgentLocation as { lat?: unknown }).lat === 'number' &&
    typeof (parsed.urgentLocation as { lng?: unknown }).lng === 'number'
      ? (parsed.urgentLocation as { lat: number; lng: number; label?: string })
      : undefined

  const sessionUuid =
    typeof parsed.sessionUuid === 'string' && parsed.sessionUuid.trim()
      ? parsed.sessionUuid.trim()
      : undefined

  if (!displayName?.trim() && avatarUrl === undefined && !mediaType && !callPurpose && !sessionUuid) {
    return null
  }
  return {
    callerDisplayName: displayName?.trim() || undefined,
    callerAvatarUrl: avatarUrl ?? undefined,
    mediaType,
    callPurpose,
    urgentLocation,
    ...(sessionUuid ? { sessionUuid } : {}),
  }
}

/**
 * END payload `detail` for a call ended because the peer vanished (or the
 * meeting ended) rather than a hang-up — the reason stays COMPLETED for the
 * server; the receiving client reads this to tell it apart.
 */
export const PEER_LOST_END_DETAIL = 'peer-lost'

export type CallEndPayload = {
  reason?: string
  startedAt?: string
  endedAt?: string
  detail?: string
  callPurpose?: 'normal' | 'on-call' | 'urgent'
  urgentLocation?: { lat: number; lng: number; label?: string } | null
  mediaType?: 'voice' | 'video'
}

export function buildCallEndPayload(
  reason: string,
  startedAt?: number,
  detail?: string,
  extra?: Pick<CallEndPayload, 'callPurpose' | 'urgentLocation' | 'mediaType'>,
): CallEndPayload {
  const endedAt = new Date().toISOString()
  return {
    reason,
    startedAt: startedAt ? new Date(startedAt).toISOString() : endedAt,
    endedAt,
    detail,
    ...(extra?.callPurpose ? { callPurpose: extra.callPurpose } : {}),
    ...(extra?.urgentLocation ? { urgentLocation: extra.urgentLocation } : {}),
    ...(extra?.mediaType ? { mediaType: extra.mediaType } : {}),
  }
}

/** Best-effort signal when the tab is closing (fetch keepalive). */
async function publishVoiceCallSignalKeepalive(
  input: PublishVoiceCallSignalInput,
): Promise<void> {
  const url = env.appsyncGraphqlUrl
  if (!url) return
  const token = await getAccessToken()
  if (!token) return

  const body = JSON.stringify({
    query: publishVoiceCallSignalMutation,
    variables: { input: toGraphqlInput(input) },
  })

  try {
    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body,
      keepalive: true,
    })
  } catch {
    /* tab may already be gone */
  }
}

/** Best-effort END signal when the tab is closing (fetch keepalive). */
export async function publishVoiceCallEndKeepalive(
  input: Omit<PublishVoiceCallSignalInput, 'type' | 'payload'> & {
    payload: CallEndPayload
  },
): Promise<void> {
  await publishVoiceCallSignalKeepalive({ ...input, type: 'END' })
}

/** Drop this device's call claim on tab close so siblings never prompt to switch. */
export async function publishDeviceClaimKeepalive(input: {
  callId: string
  callerId: string
  calleeId: string
  conversationId?: string | null
  platform: 'ios' | 'android' | 'web'
  deviceId: string
}): Promise<void> {
  await publishVoiceCallSignalKeepalive({
    callId: input.callId,
    type: 'DEVICE_CLAIM',
    callerId: input.callerId,
    calleeId: input.calleeId,
    conversationId: input.conversationId,
    senderDeviceId: input.deviceId,
    payload: {
      action: 'release',
      platform: input.platform,
      deviceId: input.deviceId,
    },
  })
}
